import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Select, Button, Spin, DatePicker, message } from 'antd';
import { getBranchWiseInvestmentSummary } from './GeneralService';
import { errHandler } from '../../helper/helper';
import BranchWiseInvestmentExcelExport from "../../Utils/BranchWiseInvestmentExcelExport";
import moment from 'moment';

const { Option } = Select;
const { RangePicker } = DatePicker;

const BranchWiseInvestmentSummary = ({ loginPlaceId, districtData, districtName, divisionalId, branchId }) => {
    const [selections, setSelections] = useState({
        districts: [],
        divisionalAreas: [],
        branches: [],
        startDate: null,
        endDate: null,
        timePeriod: 1
    });

    const [availableDivisionalAreas, setAvailableDivisionalAreas] = useState([]);
    const [availableBranches, setAvailableBranches] = useState([]);
    const [loading, setLoading] = useState(false);

    const timePeriodOptions = [
        { value: 1, label: 'All Periods' },
        { value: 7, label: '7 Days' },
        { value: 30, label: '30 Days' },
        { value: 90, label: '90 Days' },
        { value: 180, label: '180 Days' },
        { value: 360, label: '360 Days' }
    ];

    // Initialize selections based on login
    useEffect(() => {
        if (loginPlaceId === "1" && branchId) {
            setSelections(prev => ({ ...prev, branches: [parseInt(branchId)] }));
        } else if (loginPlaceId === "2" && divisionalId) {
            setSelections(prev => ({ ...prev, divisionalAreas: [parseInt(divisionalId)] }));
        } else if (loginPlaceId === "4" && districtName) {
            const userDistrict = districtData.find(d => d.districtName === districtName);
            if (userDistrict) setSelections(prev => ({ ...prev, districts: [userDistrict.districtId] }));
        }
    }, [loginPlaceId, branchId, divisionalId, districtName, districtData]);

    // Filter divisional areas based on selected districts
    useEffect(() => {
        let filteredDivs = [];

        if (loginPlaceId === "2" && divisionalId) {
            filteredDivs = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .filter(div => div.divAId === parseInt(divisionalId));
        } else if (selections.districts.length > 0) {
            filteredDivs = districtData
                .filter(d => selections.districts.includes(d.districtId))
                .flatMap(d => d.divisionalAreaDtoList || []);
        } else {
            filteredDivs = districtData.flatMap(d => d.divisionalAreaDtoList || []);
        }

        setAvailableDivisionalAreas(filteredDivs);
        setSelections(prev => ({ ...prev, branches: [] }));
    }, [selections.districts, districtData, loginPlaceId, divisionalId]);

    // Filter branches based on selected divisional areas or districts
    useEffect(() => {
        let filteredBranches = [];

        if (loginPlaceId === "1" && branchId) {
            const branch = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .flatMap(div => div.branchDtoList || [])
                .find(b => b.id === parseInt(branchId));
            if (branch) filteredBranches = [branch];
        } else if (selections.divisionalAreas.length > 0) {
            filteredBranches = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .filter(div => selections.divisionalAreas.includes(div.divAId))
                .flatMap(div => div.branchDtoList || []);
        } else if (selections.districts.length > 0) {
            filteredBranches = districtData
                .filter(d => selections.districts.includes(d.districtId))
                .flatMap(d => d.divisionalAreaDtoList || [])
                .flatMap(div => div.branchDtoList || []);
        } else {
            filteredBranches = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .flatMap(div => div.branchDtoList || []);
        }

        setAvailableBranches(filteredBranches);
    }, [selections.divisionalAreas, selections.districts, districtData, loginPlaceId, branchId, divisionalId]);

    const disabledDate = (current) => current && current > moment().endOf('day');

    const handleDistrictChange = (values) => {
        if (values.includes("ALL")) {
            const allDistricts = loginPlaceId === "4"
                ? districtData.filter(d => d.districtName === districtName).map(d => d.districtId)
                : districtData.map(d => d.districtId);
            setSelections(prev => ({ ...prev, districts: allDistricts, divisionalAreas: [], branches: [] }));
        } else {
            setSelections(prev => ({ ...prev, districts: values, divisionalAreas: [], branches: [] }));
        }
    };

    const handleDivAreaChange = (values) => {
        if (values.includes("ALL")) {
            setSelections(prev => ({ ...prev, divisionalAreas: availableDivisionalAreas.map(d => d.divAId), branches: [] }));
        } else {
            setSelections(prev => ({ ...prev, divisionalAreas: values, branches: [] }));
        }
    };

    const handleBranchChange = (values) => {
        if (values.includes("ALL")) {
            setSelections(prev => ({ ...prev, branches: availableBranches.map(b => b.id) }));
        } else {
            setSelections(prev => ({ ...prev, branches: values }));
        }
    };

    const handleDateChange = (dates) => {
        setSelections(prev => ({
            ...prev,
            startDate: dates ? dates[0] : null,
            endDate: dates ? dates[1] : null
        }));
    };

    const handleTimePeriodChange = (value) => {
        setSelections(prev => ({ ...prev, timePeriod: value }));
    };

    const generateReport = async () => {
        if (!selections.startDate || !selections.endDate) {
            message.warning("Please select date range");
            return;
        }

        if (selections.endDate.isBefore(selections.startDate)) {
            message.warning("End date must be after start date");
            return;
        }

        if (selections.branches.length === 0 && loginPlaceId !== "1") {
            message.warning("Please select at least one branch");
            return;
        }

        setLoading(true);

        try {
            const districtId = selections.districts[0] || null;
            const divAId = selections.divisionalAreas[0] || null;
            const branchIdList = loginPlaceId === "1"
                ? [parseInt(branchId)]
                : selections.branches.map(id => parseInt(id));

            const payload = {
                districtId,
                divAId,
                branchIds: branchIdList,
                selectedDate: selections.startDate.format("YYYY-MM-DD"),
                endDate: selections.endDate.format("YYYY-MM-DD"),
                timePeriod: selections.timePeriod
            };

            const response = await getBranchWiseInvestmentSummary(payload);
            let responseData = response.data || response;

            if (!responseData) {
                message.info("No data found for the selected criteria");
                return;
            }

            let dataArray = [];
            if (Array.isArray(responseData)) dataArray = responseData;
            else if (responseData.content) dataArray = responseData.content;
            else if (typeof responseData === "object") {
                dataArray = Object.values(responseData).filter(item => item && (item.bankId || item.bankName));
            }

            if (!Array.isArray(dataArray) || dataArray.length === 0) {
                message.info("No investment data found for the selected criteria");
                return;
            }

            const processedData = dataArray.map((item, index) => {
                const branchIdFromItem = item.branchId || item.branchIds?.[0];
                const branchInfo = availableBranches.find(b => b.id === branchIdFromItem);

                let districtNameFromItem = item.districtName || '';
                if (!districtNameFromItem && branchInfo) {
                    for (const district of districtData) {
                        for (const divArea of district.divisionalAreaDtoList || []) {
                            for (const branch of divArea.branchDtoList || []) {
                                if (branch.id === branchInfo.id) {
                                    districtNameFromItem = district.districtName;
                                    break;
                                }
                            }
                            if (districtNameFromItem) break;
                        }
                        if (districtNameFromItem) break;
                    }
                }

                return {
                    bankId: item.bankId || index + 1,
                    bankName: item.bankName || `Bank ${index + 1}`,
                    branchId: branchIdFromItem || branchInfo?.id || '',
                    branchName: branchInfo?.branchName || item.branchNames || 'Unknown Branch',
                    districtName: districtNameFromItem || 'Unknown District',
                    tbill: Number(item.tbill) || 0,
                    fixed: Number(item.fixed) || 0,
                    tbonds: Number(item.tbonds) || 0,
                    repos: Number(item.repos) || 0,
                    other: Number(item.other) || 0,
                    total: Number(item.total) || 0,
                    percentage: Number(item.percentage) || 0
                };
            });

            const selectedBranchNames = availableBranches
                .filter(b => selections.branches.includes(b.id))
                .map(b => b.branchName);

            const timePeriodLabel = timePeriodOptions.find(opt => opt.value === selections.timePeriod)?.label || "All Periods";

            const meta = {
                startDate: selections.startDate.format("DD/MM/YYYY"),
                endDate: selections.endDate.format("DD/MM/YYYY"),
                timePeriod: timePeriodLabel
            };

            await BranchWiseInvestmentExcelExport({
                data: processedData,
                selectedBranches: selectedBranchNames,
                loginPlaceId,
                meta
            });

        } catch (err) {
            errHandler(err);
            message.error("Failed to generate report");
        } finally {
            setLoading(false);
        }
    };

    const isGenerateDisabled = loading || !selections.startDate || !selections.endDate || (loginPlaceId !== "1" && selections.branches.length === 0);

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">

                    {/* Districts */}
                    {(loginPlaceId === "3" || loginPlaceId === "4") && (
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Form.Item label="Select Districts">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select districts"
                                        style={{ width: '100%' }}
                                        value={selections.districts}
                                        onChange={handleDistrictChange}
                                        disabled={loginPlaceId === "4"}
                                        showSearch
                                        filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                                    >
                                        {loginPlaceId === "4"
                                            ? districtData.filter(d => d.districtName === districtName).map(d => (
                                                <Option key={d.districtId} value={d.districtId}>{d.districtName}</Option>
                                            ))
                                            : <>
                                                <Option key="ALL" value="ALL">Select All Districts</Option>
                                                {districtData.map(d => (
                                                    <Option key={d.districtId} value={d.districtId}>{d.districtName}</Option>
                                                ))}
                                            </>
                                        }
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    {/* Divisional Areas */}
                    {(loginPlaceId === "2" || loginPlaceId === "3" || loginPlaceId === "4") && (
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Form.Item label="Select Divisional Areas">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select divisional areas"
                                        style={{ width: '100%' }}
                                        value={selections.divisionalAreas}
                                        onChange={handleDivAreaChange}
                                        disabled={availableDivisionalAreas.length === 0 || loginPlaceId === "2"}
                                    >
                                        <Option key="ALL" value="ALL">Select All Divisional Areas</Option>
                                        {availableDivisionalAreas.map(d => (
                                            <Option key={d.divAId} value={d.divAId}>{d.divAIdName}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    {/* Branches */}
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item label="Select Branches">
                                <Select
                                    mode="multiple"
                                    placeholder="Select branches"
                                    style={{ width: '100%' }}
                                    value={selections.branches}
                                    onChange={handleBranchChange}
                                    disabled={availableBranches.length === 0 || loginPlaceId === "1"}
                                >
                                    <Option key="ALL" value="ALL">Select All Branches</Option>
                                    {availableBranches.map(b => (
                                        <Option key={b.id} value={b.id}>{b.branchName}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Date Range */}
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item label="Select Investment Date Range">
                                <RangePicker
                                    style={{ width: '100%' }}
                                    value={[selections.startDate, selections.endDate]}
                                    onChange={handleDateChange}
                                    format="YYYY-MM-DD"
                                    disabledDate={disabledDate}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Time Period */}
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item label="Investment Maturity Period">
                                <Select
                                    value={selections.timePeriod}
                                    onChange={handleTimePeriodChange}
                                    style={{ width: '100%' }}
                                >
                                    {timePeriodOptions.map(opt => (
                                        <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Generate Button */}
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    onClick={generateReport}
                                    disabled={isGenerateDisabled}
                                    className="generate-btn"
                                    style={{ width: '100%' }}
                                    type="primary"
                                >
                                    {loading ? <Spin /> : 'Generate Report'}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>

                </Form>
            </Card>
        </div>
    );
};

export default BranchWiseInvestmentSummary;
